import { useEffect, useMemo, useState } from "react";
import type { JSONContent } from "@tiptap/core";
import { ChevronDown, ChevronUp, LoaderCircle, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { LoreEditor } from "@/editors/rich-text/lore-editor";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatLoreErrorMessage, type ProjectDocumentView } from "@/features/lore";

type ProjectDocumentEditorProps = {
  document: ProjectDocumentView;
  onSave: (content: JSONContent) => Promise<void>;
};

type SaveState = "error" | "ready" | "saved" | "saving";

const SAVE_DELAY_MS = 800;
const DOCUMENT_HEADER_STORAGE_KEY_PREFIX = "lore:document-header-collapsed:";

function serializeDocument(content: JSONContent) {
  return JSON.stringify(content);
}

function readCollapsedState(kind: string) {
  if (typeof window === "undefined") {
    return false;
  }

  return window.localStorage.getItem(`${DOCUMENT_HEADER_STORAGE_KEY_PREFIX}${kind}`) === "true";
}

export function ProjectDocumentEditor({
  document,
  onSave,
}: ProjectDocumentEditorProps) {
  const { t } = useTranslation();
  const [content, setContent] = useState(document.body);
  const [collapsed, setCollapsed] = useState(() => readCollapsedState(document.kind));
  const [lastSavedSnapshot, setLastSavedSnapshot] = useState(() => serializeDocument(document.body));
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<SaveState>("ready");
  const contentSnapshot = useMemo(() => serializeDocument(content), [content]);
  const toggleLabel = t("lore.documents.headerToggle", {
    defaultValue: collapsed ? "Expand header" : "Collapse header",
  });
  const statusContent = saveState === "saving"
    ? (
      <>
        <LoaderCircle className="h-4 w-4 animate-spin" />
        {t("lore.save.saving")}
      </>
    )
    : saveState === "error"
      ? (
        <>
          <TriangleAlert className="h-4 w-4" />
          {t("lore.save.error")}
        </>
      )
      : null;

  useEffect(() => {
    setContent(document.body);
    setCollapsed(readCollapsedState(document.kind));
    setLastSavedSnapshot(serializeDocument(document.body));
    setSaveError(null);
    setSaveState("ready");
  }, [document.body, document.id, document.kind, document.updatedAt]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(`${DOCUMENT_HEADER_STORAGE_KEY_PREFIX}${document.kind}`, String(collapsed));
  }, [collapsed, document.kind]);

  useEffect(() => {
    if (contentSnapshot === lastSavedSnapshot || saveState === "saving") {
      return;
    }

    const timeout = window.setTimeout(async () => {
      setSaveState("saving");

      try {
        await onSave(content);
        setLastSavedSnapshot(contentSnapshot);
        setSaveError(null);
        setSaveState("saved");
      } catch (error) {
        setSaveError(formatLoreErrorMessage(error));
        setSaveState("error");
      }
    }, SAVE_DELAY_MS);

    return () => window.clearTimeout(timeout);
  }, [content, contentSnapshot, lastSavedSnapshot, onSave, saveState]);

  return (
    <section className={collapsed ? "space-y-3" : "space-y-6"}>
      <header className="space-y-3 px-1 transition-all duration-300 ease-out">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className={collapsed ? "min-w-0" : "space-y-4"}>
              <div className={collapsed ? "flex min-w-0 flex-wrap items-center gap-3" : "space-y-3"}>
                <h2
                  className={collapsed
                    ? "truncate font-display text-[clamp(1.55rem,2.2vw,2rem)] leading-none text-foreground transition-all duration-300 ease-out"
                    : "font-display text-[clamp(2.4rem,4vw,3.8rem)] leading-[0.95] text-foreground transition-all duration-300 ease-out"}
                >
                  {t(document.titleKey)}
                </h2>

                <div className={collapsed ? "flex shrink-0 flex-wrap items-center gap-2" : "flex flex-wrap items-center gap-2"}>
                  {document.badgeKeys.map((badgeKey) => (
                    <Badge
                      key={badgeKey}
                      variant="outline"
                      className={collapsed
                        ? "rounded-full border-clay/15 bg-white/70 px-2.5 py-0.5 text-[0.6rem] uppercase tracking-[0.16em] text-clay transition-all duration-300 ease-out"
                        : "rounded-full border-clay/15 bg-white/70 px-3 py-1 text-[0.7rem] uppercase tracking-[0.18em] text-clay transition-all duration-300 ease-out"}
                    >
                      {t(badgeKey)}
                    </Badge>
                  ))}
                </div>
              </div>

              <div
                className={collapsed
                  ? "pointer-events-none max-h-0 overflow-hidden opacity-0 -translate-y-2 transition-all duration-300 ease-out"
                  : "max-h-40 overflow-hidden opacity-100 translate-y-0 transition-all duration-300 ease-out"}
              >
                <p className="max-w-4xl text-lg leading-8 text-clay">
                  {t(document.descriptionKey)}
                </p>
              </div>
            </div>
          </div>

          <div className="flex shrink-0 items-start gap-3">
            <Button
              aria-label={toggleLabel}
              className="h-8 w-8 rounded-full border border-[#dcccb7] bg-white p-0 text-muted-foreground shadow-none transition-all duration-300 ease-out hover:bg-[#f8f3eb] hover:text-foreground"
              onClick={() => setCollapsed((current) => !current)}
              type="button"
              variant="ghost"
            >
              <span className="transition-transform duration-300 ease-out">
                {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
              </span>
            </Button>

            {statusContent ? (
              <p
                aria-live="polite"
                className="inline-flex items-center gap-2 pt-1 text-sm whitespace-nowrap text-muted-foreground transition-all duration-300 ease-out"
              >
                {statusContent}
              </p>
            ) : null}
          </div>
        </div>

        {saveError ? (
          <p className="rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {t(saveError)}
          </p>
        ) : null}
      </header>

      <LoreEditor
        autofocus
        content={content}
        onChange={setContent}
        placeholder={t("lore.documents.editorPlaceholder")}
      />
    </section>
  );
}
